'use client';

import { AccountHoverCard } from '#/components/ethereum/account-hover-card';
import {
  useIsWalletMatch,
  WalletInput,
} from '#/components/ethereum/wallet-input';
import { Alert, AlertDescription, AlertTitle } from '#/components/ui/alert';
import { Button } from '#/components/ui/button';
import { Checkbox } from '#/components/ui/checkbox';
import { DialogFooter } from '#/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '#/components/ui/form';
import { Label } from '#/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '#/components/ui/select';
import { HelpTooltip } from '#/components/v2/help-tooltip';
import { useTranslation } from '#/contexts/useTranslation';
import { zodResolver } from '@hookform/resolvers/zod';
import { useActor } from '@xstate/react';
import { Translation } from 'intl-lib';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { useContext, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { MachineContext } from './contexts/machine';

export function StakingDialogConfirmation() {
  const translation = useTranslation(
    translation => translation.EthereumStaking.StakingAction
  );
  const translationEthereumStaking = useTranslation(
    translation => translation.EthereumStaking
  );
  const translationApplication = useTranslation(
    translation => translation.Application
  );

  const interpreter = useContext(MachineContext);
  const [state, dispatch] = useActor(interpreter);
  const { withdrawaddr, claimaddr, count, amount } = state.context;
  const isLoading = state.hasTag('loading');

  const isWalletMatch = useIsWalletMatch();

  const formSchema = useMemo(
    () =>
      z.object({
        overrideGasLimit: z.string(),
        // .refine(val => val >= 1 && val <= 2, {
        //   message: translation.Confirmation.OverrideGasLimit.ErrorMessage,
        // }),
        agreement: z.boolean().refine(val => val === true, {
          message: translation.Confirmation.agreement.errorMessage,
        }),
      }),
    [translation]
  );
  // 1. Define your form.
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      overrideGasLimit: '1',
      agreement: false,
    },
  });

  // 2. Define a submit handler.
  async function onSubmit(values: z.infer<typeof formSchema>) {
    // Do something with the form values.
    // ✅ This will be type-safe and validated.
    console.log('onSubmit', values);
    const { overrideGasLimit } = values;
    dispatch({
      type: 'NEXT',
      data: {
        overrideGasLimit,
      },
    });
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <div className="flex flex-col gap-2">
          <Label>
            <Translation path="EthereumStaking.StakingAction.Confirmation.FundingAddress.Label" />
          </Label>
          <div>
            <WalletInput />
          </div>
        </div>
        <dl className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            {/* count */}
            <div className="h-6 grid grid-cols-2 gap-2 items-center">
              <dt className="text-sm">
                <Translation path="EthereumStaking.StakingAction.Information.Validators.Label" />
              </dt>
              <dd className="text-sm">{count}</dd>
            </div>
            {/* amount */}
            <div className="h-6 grid grid-cols-2 gap-2 items-center">
              <dt className="text-sm">
                <Translation path="EthereumStaking.StakingAction.Information.Amount.Label" />
              </dt>
              <dd className="text-sm">{amount}</dd>
            </div>
            {/* total */}
            <div className="h-6 grid grid-cols-2 gap-2 items-center">
              <dt className="text-sm">
                <Translation path="EthereumStaking.StakingAction.Information.Total.Label" />
              </dt>
              <dd className="text-sm">{count! * amount!} ETH</dd>
            </div>
            {/* withdrawaddr */}
            <div className="h-6 grid grid-cols-2 gap-2 items-center">
              <dt className="text-sm">
                <Translation path="EthereumStaking.WithdrawalAddress" />
              </dt>
              <dd className="text-sm">
                <AccountHoverCard address={withdrawaddr} visibleChars={6} />
              </dd>
            </div>
            {/* claimaddr */}
            <div className="h-6 grid grid-cols-2 gap-2 items-center">
              <dt className="text-sm">
                <Translation path="EthereumStaking.ClaimAddress" />
              </dt>
              <dd className="text-sm">
                <AccountHoverCard address={claimaddr} visibleChars={6} />
              </dd>
            </div>
            {/* FeeMEVRewardsCommission */}
            <div className="h-6 grid grid-cols-2 gap-2 items-center">
              <dt className="text-sm">
                <Translation path="EthereumStaking.StakingAction.Confirmation.FeeMEVRewardsCommission" />
              </dt>
              <dd className="text-sm">
                20%{' '}
                <HelpTooltip
                  content={
                    <Translation path="EthereumStaking.StakingAction.Confirmation.FeeMEVRewardsCommissionHelp" />
                  }
                />
              </dd>
            </div>
            {/* gasLimit */}
            <div className="h-8 grid grid-cols-2 gap-2 items-center">
              <dt className="text-sm">
                <Translation path="EthereumStaking.StakingAction.Confirmation.GasLimit" />
              </dt>
              <dd className="text-sm">
                <FormField
                  control={form.control}
                  name="overrideGasLimit"
                  render={({ field }) => (
                    <FormItem>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger size="sm">
                            <SelectValue placeholder="Select a gas limit" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="1">
                            100% {translation.Confirmation.GasLimitDefault}
                          </SelectItem>
                          <SelectItem value="1.2">
                            120% {translation.Confirmation.GasLimitRecommended}
                          </SelectItem>
                          <SelectItem value="1.5">150%</SelectItem>
                          {/* <SelectItem value="2">200%</SelectItem> */}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </dd>
            </div>
          </div>
          {/* reminder */}
          <div>
            <Alert className="bg-yellow-50">
              <AlertTriangle className="w-4 h-5" />
              <AlertTitle>
                <Translation path="EthereumStaking.StakingAction.Confirmation.Warning.Title" />
              </AlertTitle>
              <AlertDescription>
                <Translation path="EthereumStaking.StakingAction.Confirmation.Warning.Description" />
                <p className="mt-2 text-xs">
                  <Translation path="EthereumStaking.StakingAction.Confirmation.Warning.Disclaimers" />
                </p>
              </AlertDescription>
            </Alert>
          </div>
          {/* agreement */}
          <FormField
            control={form.control}
            name="agreement"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="hover:bg-accent/50 flex items-start gap-3 rounded-lg border p-3 has-[[aria-checked=true]]:border-blue-600 has-[[aria-checked=true]]:bg-blue-50 dark:has-[[aria-checked=true]]:border-blue-900 dark:has-[[aria-checked=true]]:bg-blue-950">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={(value: boolean) =>
                        field.onChange(value)
                      }
                      className="data-[state=checked]:border-blue-600 data-[state=checked]:bg-blue-600 data-[state=checked]:text-white dark:data-[state=checked]:border-blue-700 dark:data-[state=checked]:bg-blue-700"
                    />
                  </FormControl>
                  <div className="grid gap-1.5 font-normal">
                    <p className="text-sm leading-none font-medium">
                      <Translation path="EthereumStaking.StakingAction.Confirmation.agreement.label" />
                    </p>
                    <div className="text-muted-foreground text-sm [&_a]:text-blue-600 [&_a:hover]:underline">
                      <Translation path="EthereumStaking.StakingAction.Confirmation.agreement.details" />
                    </div>
                  </div>
                </FormLabel>
                <FormMessage />
              </FormItem>
            )}
          />
        </dl>
        <DialogFooter className="gap-2 sm:space-x-0">
          <Button
            variant="secondary"
            type="button"
            disabled={!state.can('PREV')}
            onClick={() => dispatch('PREV')}
          >
            <Translation path="EthereumStaking.StakingAction.Confirmation.BackToEditButtonLabel" />
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={!isWalletMatch || isLoading}
          >
            {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
            <Translation path="EthereumStaking.StakingAction.Confirmation.ConfirmStakeButtonLabel" />
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
